import {
	Grid,
	H2,
	InfoChip,
	MediumDialog,
	Spacer,
	Tabs,
	Text,
	Tile,
} from '@components';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
	RateCardActiveType,
	RateCardGlobalSettings,
	RateCardSettings,
	DEFAULT_MODIFIER_SET,
	DEFAULT_RULES,
	RateCardFormErrors,
	Quote,
	RateCardActiveItemId,
} from '@state';
import {
	ILane, SettingType,
	RateCardType,
	ILaneRateCard,
	CARGO_TYPE_ID,
	CAPACITY_TYPE,
	getCapacityTypeLabel,
	SPACER_DIRECTION,
} from '@types';
import styles from './RateCard.module.scss';
import { useEffect, useState } from 'react';
import {
	RCCPKIcon,
	RCFullLoadIcon,
	RCPalletsIcon,
	RCWeightIcon,
} from '@img/icons';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { ZodFormattedError } from 'zod';
import {
	convertRangesArrayToObject,
	getQuoteQuantities,
	getRateCardTypeLabel,
	getTemperatureNameById,
	rateCardTypeFromCargoTypeId,
	TLaneSchema
} from '@utils';
import { AddRateCardDropdown } from './AddRateCardDropdown';
import { RateCardContent } from './RateCardContent';
import { useClearRCSettingsError, useIsInRoute, useRateCardState, useRateCardTypesState } from '@hooks';
import registrationGif from '@img/gif/registration.gif'
import InfoIcon from '@mui/icons-material/InfoOutlined';
import AspectRatioOutlinedIcon from '@mui/icons-material/AspectRatioOutlined';
import { RateCardFuelLevyEditor } from './RateCardFuelLevyEditor';

type TConfigureRatesProps = {
	settingType: SettingType;
	isDisabled?: boolean;
	isLinkedRateCard?: boolean;
}

export const ConfigureRates = (props: TConfigureRatesProps) => {
	const {
		settingType,
		isDisabled = false,
		isLinkedRateCard = false,
	} = props;
	const [rateCard, setRateCard] = useRateCardState();
	const rcSettings = useRecoilValue(RateCardSettings);
	const rcGlobalSettings = useRecoilValue(RateCardGlobalSettings);
	const [rcActiveType, setRcActiveType] = useRecoilState(RateCardActiveType);
	const [rcActiveItemID, setRcActiveItemID] = useRecoilState(RateCardActiveItemId);
	const [rateCardToDelete, setRateCardToDelete] = useState<RateCardType | string | null>(null);
	const [isDeleteRateCardModalOpen, setIsDetdeleteRateCardModalOpen] = useState<boolean>(false)
	const quoteInRoute = useIsInRoute('quote');
	const isStep2 = useIsInRoute('step2');
	const isStep3 = useIsInRoute('step3');
	const isLinkedRateCardStep2 = isLinkedRateCard && isStep2;
	const isLinkedRateCardStep3 = isLinkedRateCard && isStep3;
	const quote = useRecoilValue(Quote);
	const isCreateRateCard = useIsInRoute('create');
	const rcFormErrors = useRecoilValue(RateCardFormErrors) as ZodFormattedError<TLaneSchema>;
	const clearRCSettingsError = useClearRCSettingsError();

	const handleDeleteRateCardClick = (rcItemIdOrType: RateCardType | string) => {
		setRateCardToDelete(rcItemIdOrType);
		setIsDetdeleteRateCardModalOpen(true);
	}

	const onCloseDialog = () => {
		setRateCardToDelete(null);
		setIsDetdeleteRateCardModalOpen(false);
	}

	const onConfirmRateCardDelete = () => {
		const updatedRateCards = rateCard?.rate_cards.filter((rc) => {
			return rc.item_definition?.item_id !== rateCardToDelete && rc.type !== rateCardToDelete
		});
		clearRCSettingsError('rate_card_prices')
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}
		setRateCard(update as ILane);
		onCloseDialog();
	}

	const possibleRateCardTypes = [
		{
			key: 'FULL_LOAD',
			label: 'Full Load',
			icon: RCFullLoadIcon,
			text: 'full load'
		},
		{
			key: 'PALLETS',
			label: 'Pallets',
			icon: RCPalletsIcon,
			text: 'pallets',
		},
		{
			key: 'WEIGHT',
			label: 'Tonnes',
			icon: RCWeightIcon,
			text: 'tonnes'
		},
		{
			key: 'CPK',
			label: 'CPK',
			icon: RCCPKIcon,
			text: 'CPK'
		},
	];

	const handleAddRateCardType = ({ rateCardType, itemId }: {
		rateCardType: RateCardType;
		itemId?: string;
	}) => {
		const selectedRateCardSettings = rcSettings?.settings?.preexisting && rcSettings?.settings.rate_card_types.find((rc) => {
			if (itemId && rc.item_definition?.item_id) return rc.item_definition?.item_id === itemId;
			return rc.type === rateCardType
		});
		const selectedRateCardGlobalSettings = rcGlobalSettings?.settings?.rate_card_types?.find((rc) => rc.type === rateCardType);
		const settingsFullLoad = rcSettings?.settings?.preexisting && rcSettings?.settings.full_load.reduce((acc, key) => {
			// @ts-ignore
			acc[key] = {
				Ambient: undefined, Chilled: undefined, Frozen: undefined
			};
			return acc;
		}, {});
		const globalSettingsFullLoad = rcGlobalSettings?.settings.full_load.reduce((acc, key) => {
			// @ts-ignore
			acc[key] = {
				Ambient: undefined, Chilled: undefined, Frozen: undefined
			};
			return acc;
		}, {});

		const templateToUse = selectedRateCardSettings ? selectedRateCardSettings : selectedRateCardGlobalSettings;

		const updatedRateCards = [
			...(rateCard?.rate_cards ? rateCard?.rate_cards as ILaneRateCard[] : []),
			{
				type: rateCardType,
				rangePrices: rateCardType !== 'FULL_LOAD' ?
					convertRangesArrayToObject(
						templateToUse?.ranges?.length ?
							templateToUse?.ranges :
							[{ start: 0, end: 0 }]
					) : settingsFullLoad ? settingsFullLoad : globalSettingsFullLoad,
				extra_attributes: {
					priced_annualized_volume: undefined,
					priced_margin: undefined
				},
				margins: {},
				modifier_set: templateToUse?.modifier_set || DEFAULT_MODIFIER_SET,
				rules: templateToUse?.rules || DEFAULT_RULES,
				item_definition: itemId ? {
					item_id: itemId,
					...templateToUse?.item_definition
				} : undefined
			}
		]
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}
		setRateCard(update as ILane);
		setRcActiveType(rateCardType);
		if (itemId) {
			setRcActiveItemID(itemId);
		}
	}

	const addRateCardTypeTab = () => {
		const arr: {
			itemId: string;
			key: string;
			label: string;
		}[] = [];
		rcSettings?.settings?.rate_card_types?.forEach((rc) => {
			if (rc.item_definition?.item_id && rc.item_definition?.is_active) {
				arr.push(
					{
						itemId: rc.item_definition?.item_id,
						key: 'ITEM',
						label: rc.item_definition?.name,
					}
				)
			}
		})
		return {
			tabTitle: <>
				<div className={styles.addRateCardContainer}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}>
					<AddRateCardDropdown
						onAddRateCardType={handleAddRateCardType}
						possibleRateCardTypes={[...possibleRateCardTypes, ...arr]}
						rateCard={rateCard}
						isDisabled={isDisabled}
					/>
				</div><span style={{ height: '42px' }}></span>
			</>,
			TabContent: <div className={styles.emptyContainer}>
				<img src={registrationGif} width={'85px'} height={'85px'} alt='Rate Card' />
				<H2>
					Choose a rate card type
				</H2>
				<Text>
					Pre-populated values are the Ofload defaults but you can overwrite them
				</Text>
				<Spacer size={10} />
				<AddRateCardDropdown
					onAddRateCardType={handleAddRateCardType}
					possibleRateCardTypes={[...possibleRateCardTypes, ...arr]}
					rateCard={rateCard}
					isDisabled={isDisabled}
				/>
			</div>,
			key: null,
			disabled: true,
		}
	}

	const [tabsState, setTabsState] = useState<{
		tabs: {
			tabTitle: string | JSX.Element,
			key: string | null,
			TabContent: JSX.Element,
			itemId?: string
		}[],
		selectedTabIndex: number
	}>({
		tabs: quoteInRoute ? [] : [addRateCardTypeTab()],
		selectedTabIndex: 0
	})

	const handleOnTabSelect = (index: number) => {
		if (index < 0 || !tabsState?.tabs.length) return;
		const rateCardTypeKey = tabsState.tabs?.[index]?.key;
		const rateCardItemId = tabsState.tabs?.[index]?.itemId;
		if (!rateCardTypeKey) {
			setRcActiveType(tabsState.tabs?.[0]?.key as RateCardType ?? null)
			return
		}
		setRcActiveItemID(rateCardItemId ?? null)
		setRcActiveType(rateCardTypeKey as RateCardType);
	}
	const [rcWithErrors, setRcWithErrors] = useState<string[]>([]);
	const [rcTypes] = useRateCardTypesState();

	const hasMultipleErrors = (obj: any): boolean => {
		if (!obj || typeof obj !== 'object') return false;

		if (Array.isArray(obj._errors) && obj._errors.length >= 1) {
			return true;
		}

		return Object.values(obj).some((value) => hasMultipleErrors(value));
	}

	const renderTabContent = (key: string, itemId: string | null) => {
		const rc = rateCard?.rate_cards.find((rc) => {
			if (itemId) return rc.item_definition?.item_id === itemId;
			return rc.type === key
		});
		if (!rc) return <></>;
		return <RateCardContent
			rc={rc}
			settingType={settingType}
			handleDeleteRateCard={handleDeleteRateCardClick}
			isDisabled={isDisabled}
			isLinkedRateCard={isLinkedRateCard}
		/>
	}

	const getIcon = (type: RateCardType, itemId: string | null) => {
		if (type === 'ITEM') {
			return <AspectRatioOutlinedIcon
				width={20}
				height={20}
				style={{ color: rcActiveType === 'ITEM' && itemId === rcActiveItemID ? 'var(--brand-orange-default)' : 'var(--neutral-07)' }}
			/>
		}
		let RCIcon;
		switch (type) {
		case 'FULL_LOAD':
			RCIcon = RCFullLoadIcon;
			break;
		case 'PALLETS':
			RCIcon = RCPalletsIcon;
			break;
		case 'WEIGHT':
			RCIcon = RCWeightIcon;
			break;
		case 'CPK':
			RCIcon = RCCPKIcon;
			break;
		default:
			RCIcon = RCPalletsIcon;
			break;
		}
		return <RCIcon width={20} height={20} color={type === rcActiveType ? 'var(--brand-orange-default)' : 'var(--neutral-07)'} />
	};
	const getLabel = (type: RateCardType, itemId: string | null) => {
		switch (type) {
		case 'FULL_LOAD':
			return 'Full Load'
		case 'PALLETS':
			return 'Pallets'
		case 'WEIGHT':
			return 'Tonnes'
		case 'CPK':
			return 'CPK'
		case 'ITEM':
			return (rcTypes.find((rc) => rc.item_definition?.item_id === itemId)?.item_definition?.name) || 'Custom Item'
		default:
			return ''
		}
	}

	const getCapacityLabel = () => {
		const cargoId = quote?.quote.cargo_type_id;
		let str = '';
		if (cargoId) {
			switch (cargoId) {
			case CARGO_TYPE_ID.FullTruckload:
				str = CAPACITY_TYPE.FTL;
				break;
			case CARGO_TYPE_ID.Volumetric:
			case CARGO_TYPE_ID.Other:
				str = CAPACITY_TYPE.VOLUMETRIC;
				break;
			default:
				str = CAPACITY_TYPE.PARTIAL
				break;
			}
		}
		if (str) {
			return getCapacityTypeLabel(str as CAPACITY_TYPE)
		}
	}


	useEffect(() => {
		if (rcFormErrors) {
			const errors: string[] = [];
			if (
				rcFormErrors.rate_card_prices &&
				Object.keys(rcFormErrors.rate_card_prices)?.length
			) {
				const keys = Object.keys(rcFormErrors.rate_card_prices);
				keys.forEach((key) => {
					// @ts-ignore
					if (hasMultipleErrors(rcFormErrors?.rate_card_prices?.[key])) {
						const typeOrItemIdWithError = rcTypes[Number(key)]?.item_definition?.item_id ?? rcTypes[Number(key)].type;
						typeOrItemIdWithError && errors.push(typeOrItemIdWithError);
					}
				});
			}
			setRcWithErrors(errors);
			return;
		}
		setRcWithErrors([]);
	}, [rcTypes, rcFormErrors])

	useEffect(() => {
		if (rateCard) {
			const newTabs: {
				tabTitle: string |JSX.Element,
				key: string,
				TabContent: JSX.Element,
				itemId?: string
			}[] = []

			const sortOrder = ['FULL_LOAD', 'PALLETS', 'WEIGHT', 'CPK', 'ITEM'];
			rateCard?.rate_cards?.forEach((rc) => {
				newTabs.push({
					tabTitle: (
						<div className={styles.tabTitle}>
							{getIcon(rc.type, rc.item_definition?.item_id ?? null)}
							<Text>
								{getLabel(rc.type, rc.item_definition?.item_id ?? null)}
							</Text>
							<span className={styles.tabContainerError}>
								{
									rc.item_definition?.item_id &&
									rcWithErrors.includes(rc.item_definition?.item_id) && <ErrorOutlineOutlinedIcon />
								}
								{
									!rc.item_definition?.item_id &&
									rcWithErrors.includes(rc.type) && <ErrorOutlineOutlinedIcon />
								}
							</span>
						</div>
					),
					key: rc.type,
					TabContent: <div style={{ minHeight: '300px', overflowX: 'auto' }} key={rc.type}>
						{renderTabContent(rc.type, rcActiveType === 'ITEM' ? rcActiveItemID : null)}
					</div>,
					itemId: rc.item_definition?.item_id
				})
			})
			newTabs.sort((a, b) => sortOrder.indexOf(a.key) - sortOrder.indexOf(b.key));
			const index = newTabs.findIndex((tab) => {
				if (rcActiveType === 'ITEM' && rcActiveItemID) return tab.itemId === rcActiveItemID;
				if (rcActiveType === 'ITEM' && quoteInRoute) return tab.itemId === quote?.quote_shipment.cargos[0].cargo_item?.item_id;
				return tab.key === rcActiveType
			});
			let selectedTabIndex = 0;
			if (index >= 0) {
				selectedTabIndex = index + (quoteInRoute ? 0 : 1);
				setRcActiveType(newTabs[index].key as RateCardType);
				if (!rcActiveItemID && newTabs[index].itemId) {
					setRcActiveItemID(newTabs[index].itemId)
				}
			} else if (!isCreateRateCard && newTabs.length >= 2) {
				selectedTabIndex = 1;
				setRcActiveType(newTabs[1].key as RateCardType);
				if (!rcActiveItemID && newTabs[1].itemId) {
					setRcActiveItemID(newTabs[1].itemId)
				}
			}
			setTabsState({
				tabs: [...(quoteInRoute ? [] : [addRateCardTypeTab()]), ...newTabs],
				selectedTabIndex
			});
		}
	}, [rateCard, rcWithErrors, rcActiveType, rcActiveItemID]);

	return (
		<>
			<Tile stretch>
				<Grid columns={2} columnTemplate={'auto 200px'}>
					<Grid.Item>
						{/* Only display carrier or shipper ratecard for linked rate card flow, otherwise keep the old UI and only display "Rate cards" */}
						<H2>{
							isLinkedRateCardStep2
								? 'Carrier '
								: isLinkedRateCardStep3
									? 'Shipper '
									: ''
						}Rate cards
						</H2>
						<Text>
							Establish lane-level values. These are the most granular values.
						</Text>
					</Grid.Item>

					<Grid.Item justifySelf='end'>
						<RateCardFuelLevyEditor isDisabled={isDisabled} />
					</Grid.Item>
				</Grid>
				<Spacer size={20} />
				{
					isLinkedRateCardStep2 && <InfoChip
						stretch={true}
						title={'Configuration Applies to Both Carrier & Shipper'}
						text={'The values entered below serve as the locked base for both rate cards. On the next page, you will need to apply the Shipper’s booking rules.'}
					/>
				}
				{
					isLinkedRateCardStep3 && <InfoChip
						stretch={true}
						title={''}
						text={'Lane values are inherited directly from the Carrier Rate Card and cannot be edited here. Please configure the rules below to finalise the Shipper Rate Card.'}
					/>
				}
				{
					quoteInRoute && quote && <>
						<Spacer size={20} />
						<div className={styles.quoteData}>
							<Grid
								columns={3}
								columnTemplate='repeat(3, max-content)'
								columnGap='32px'
								rowGap='8px'
							>
								<Grid.Item columnSpan={3}>
									<Grid columns={2} alignItems='center' columnTemplate='20px auto'>
										<InfoIcon />
										<Text>The Shipper has requested the following:</Text>
									</Grid>
								</Grid.Item>
								<Grid.Item>
									<Grid columns={2} alignItems='center' columnTemplate='20px auto'>

										<Spacer size={20} dir={SPACER_DIRECTION.HORIZONTAL} />
										<div>
											<Text>Capacity type: {getCapacityLabel()}</Text>
											<Text>Cargo type: {
												quote.quote.cargo_type_id === CARGO_TYPE_ID.CustomItem ?
													quote.quote_shipment?.cargos?.[0]?.cargo_item?.name ?? '-' :
													getRateCardTypeLabel(rateCardTypeFromCargoTypeId(quote.quote.cargo_type_id) as RateCardType)}
											</Text>
										</div>
									</Grid>
								</Grid.Item>
								<Grid.Item>
									<Text>Qty: {getQuoteQuantities('pallet_qty', quote)}</Text>
									{
										quote.quote.cargo_type_id === CARGO_TYPE_ID.CustomItem &&
										quote.cargo?.[0]?.total_volume ?
											<Text>Cubic: {quote.cargo?.[0]?.total_volume.toFixed(2)} m³</Text> :
											<Text>Spaces: {getQuoteQuantities('qty', quote)}</Text>
									}
								</Grid.Item>
								<Grid.Item>
									<Text>Temperature: {getTemperatureNameById(quote.quote.temperature_id)}</Text>
									<Text>Total weight (kg): {getQuoteQuantities('total_weight', quote)}</Text>
								</Grid.Item>
							</Grid>
						</div>
					</>
				}
				<Spacer size={20} />
				<Tabs
					tabs={tabsState.tabs}
					borderRadius={'8px'}
					showContentBorder
					isVertical
					showActiveIndicator={!!tabsState.tabs.length}
					onSelect={handleOnTabSelect}
					selectedTab={tabsState.selectedTabIndex}
				/>
			</Tile>
			<MediumDialog
				title={`Delete the ${getLabel(rcActiveType as RateCardType, rcActiveItemID)} rate card type?`}
				text={''}
				slot={<>
					<Text>
						New {
							rcActiveType === 'ITEM' ?
								getLabel(rcActiveType as RateCardType, rcActiveItemID) :
								getLabel(rcActiveType as RateCardType, rcActiveItemID).toLowerCase()
						} bookings require
						a {
							rcActiveType === 'ITEM' ?
								getLabel(rcActiveType as RateCardType, rcActiveItemID) :
								getLabel(rcActiveType as RateCardType, rcActiveItemID).toLowerCase()
						} rate card type.
						Existing shipments will not be affected.
					</Text>
				</>}
				open={isDeleteRateCardModalOpen}
				onClose={() => onCloseDialog()}
				actionButtonFn={onConfirmRateCardDelete}
				actionButtonText={'Delete'}
			/>
		</>
	);
}
